/**
 * CarrierSelector Component
 *
 * Search and select carriers for merger analysis.
 * Supports DOT number and company name search.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Search,
  Plus,
  X,
  Truck,
  AlertCircle,
  Loader2,
  ChevronRight,
} from 'lucide-react';
import { mergerApi } from '../../services/merger.api';
import { logger } from '../../utils/logger';

// Selected carrier type
export interface SelectedCarrier {
  dotNumber: number;
  legalName: string;
  dbaName?: string;
  totalDrivers: number;
  totalPowerUnits: number;
  safetyRating?: string;
  statusCode?: string;
}

interface CarrierSelectorProps {
  selectedCarriers: SelectedCarrier[];
  onCarriersChange: (carriers: SelectedCarrier[]) => void;
  onAnalyze: () => void;
  isAnalyzing: boolean;
  minCarriers?: number;
  maxCarriers?: number;
}

interface SearchResult {
  dotNumber: number;
  legalName: string;
  dbaName?: string;
  totalDrivers: number;
  totalPowerUnits: number;
  safetyRating?: string;
  statusCode?: string;
  phyCity?: string;
  phyState?: string;
}

const CarrierSelector: React.FC<CarrierSelectorProps> = ({
  selectedCarriers,
  onCarriersChange,
  onAnalyze,
  isAnalyzing,
  minCarriers = 2,
  maxCarriers = 10,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced search
  const performSearch = useCallback(async (query: string) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    setIsSearching(true);
    setSearchError(null);

    try {
      // Determine search type
      const isNumeric = /^\d+$/.test(query);
      const searchType = isNumeric ? 'dot' : 'name';

      const response = await mergerApi.searchCarriers(query, searchType);

      // Handle various API response formats
      // The API already returns an array, but handle wrapped responses just in case
      let resultsArray: unknown[] = [];
      if (Array.isArray(response)) {
        resultsArray = response;
      } else if (response && typeof response === 'object') {
        const resp = response as Record<string, unknown>;
        if (resp.results && Array.isArray(resp.results)) {
          resultsArray = resp.results;
        } else if (resp.data && typeof resp.data === 'object') {
          const data = resp.data as Record<string, unknown>;
          if (data.results && Array.isArray(data.results)) {
            resultsArray = data.results;
          }
        }
      }

      // Transform snake_case API response to camelCase for component use
      const transformedResults: SearchResult[] = resultsArray.slice(0, 10).map((item: unknown) => {
        const record = item as Record<string, unknown>;
        return {
          dotNumber: (record.dotNumber ?? record.dotNumber) as number,
          legalName: (record.legalName ?? record.legalName) as string,
          dbaName: (record.dbaName ?? record.dbaName) as string | undefined,
          totalDrivers: (record.total_drivers ?? record.totalDrivers ?? record.drivers ?? 0) as number,
          totalPowerUnits: (record.power_units ?? record.totalPowerUnits ?? record.total_power_units ?? 0) as number,
          safetyRating: (record.safety_rating ?? record.safetyRating) as string | undefined,
          statusCode: (record.status_code ?? record.statusCode) as string | undefined,
          phyCity: (record.physical_city ?? record.phyCity ?? record.phyCity) as string | undefined,
          phyState: (record.physical_state ?? record.phyState ?? record.phyState) as string | undefined,
        };
      });

      setSearchResults(transformedResults); // Limit to 10 results already applied
      setShowResults(true);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Search failed';
      logger.error('Carrier search error:', { error, searchQuery });
      setSearchError('Failed to search carriers. Please try again.');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Handle search input change with debounce
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const query = e.target.value;
      setSearchQuery(query);

      // Clear previous timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Debounce search
      searchTimeoutRef.current = setTimeout(() => {
        performSearch(query);
      }, 300);
    },
    [performSearch]
  );

  // Add carrier to selection
  const handleAddCarrier = useCallback(
    (carrier: SearchResult) => {
      // Check if already selected
      if (selectedCarriers.some((c) => c.dotNumber === carrier.dotNumber)) {
        setSearchError('This carrier is already selected.');
        return;
      }

      // Check max limit
      if (selectedCarriers.length >= maxCarriers) {
        setSearchError(`Maximum ${maxCarriers} carriers allowed.`);
        return;
      }

      const newCarrier: SelectedCarrier = {
        dotNumber: carrier.dotNumber,
        legalName: carrier.legalName,
        dbaName: carrier.dbaName,
        totalDrivers: carrier.totalDrivers,
        totalPowerUnits: carrier.totalPowerUnits,
        safetyRating: carrier.safetyRating,
        statusCode: carrier.statusCode,
      };

      onCarriersChange([...selectedCarriers, newCarrier]);
      setSearchQuery('');
      setSearchResults([]);
      setShowResults(false);
      setSearchError(null);
    },
    [selectedCarriers, maxCarriers, onCarriersChange]
  );

  // Remove carrier from selection
  const handleRemoveCarrier = useCallback(
    (dotNumber: number) => {
      onCarriersChange(selectedCarriers.filter((c) => c.dotNumber !== dotNumber));
    },
    [selectedCarriers, onCarriersChange]
  );

  // Clear all selected carriers
  const handleClearAll = useCallback(() => {
    onCarriersChange([]);
  }, [onCarriersChange]);

  // Close results on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        resultsRef.current &&
        !resultsRef.current.contains(event.target as Node) &&
        !searchInputRef.current?.contains(event.target as Node)
      ) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const canAnalyze = selectedCarriers.length >= minCarriers && !isAnalyzing;

  return (
    <div className="card-modern" style={{ marginBottom: 'var(--space-xl)' }}>
      {/* Header */}
      <div style={{ marginBottom: 'var(--space-lg)' }}>
        <h2
          className="heading-3"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-sm)',
          }}
        >
          <span
            style={{
              width: 28,
              height: 28,
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-primary)',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.875rem',
              fontWeight: 600,
            }}
          >
            1
          </span>
          Select Carriers for Merger Analysis
        </h2>
        <p className="text-muted" style={{ marginTop: 'var(--space-xs)' }}>
          Search and add {minCarriers} to {maxCarriers} carriers to analyze a potential merger or acquisition.
        </p>
      </div>

      {/* Search Input */}
      <div style={{ position: 'relative', marginBottom: 'var(--space-md)' }}>
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-sm)',
          }}
        >
          <div style={{ position: 'relative', flex: 1 }}>
            <Search
              style={{
                position: 'absolute',
                left: '1rem',
                top: '50%',
                transform: 'translateY(-50%)',
                width: 20,
                height: 20,
                color: 'var(--color-gray-400)',
              }}
            />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={() => searchResults.length > 0 && setShowResults(true)}
              placeholder="Search by DOT number or company name..."
              disabled={selectedCarriers.length >= maxCarriers}
              style={{
                width: '100%',
                padding: '0.75rem 1rem 0.75rem 3rem',
                fontSize: '0.9375rem',
                border: '1px solid var(--color-gray-300)',
                borderRadius: 'var(--radius-lg)',
                outline: 'none',
                transition: 'all 0.15s ease',
                backgroundColor: selectedCarriers.length >= maxCarriers ? 'var(--color-gray-100)' : 'white',
              }}
            />
            {isSearching && (
              <Loader2
                style={{
                  position: 'absolute',
                  right: '1rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 20,
                  height: 20,
                  color: 'var(--color-primary)',
                  animation: 'spin 1s linear infinite',
                }}
              />
            )}
          </div>
        </div>

        {/* Search Results Dropdown */}
        {showResults && searchResults.length > 0 && (
          <div
            ref={resultsRef}
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              marginTop: 'var(--space-xs)',
              background: '#fbf9f4',
              border: '1px solid var(--color-gray-200)',
              borderRadius: 'var(--radius-lg)',
              boxShadow: 'var(--shadow-lg)',
              zIndex: 50,
              maxHeight: '400px',
              overflowY: 'auto',
            }}
          >
            {searchResults.map((result) => {
              const isSelected = selectedCarriers.some(
                (c) => c.dotNumber === result.dotNumber
              );
              return (
                <div
                  key={result.dotNumber}
                  onClick={() => !isSelected && handleAddCarrier(result)}
                  style={{
                    padding: 'var(--space-md)',
                    borderBottom: '1px solid var(--color-gray-100)',
                    cursor: isSelected ? 'not-allowed' : 'pointer',
                    opacity: isSelected ? 0.5 : 1,
                    backgroundColor: isSelected ? 'var(--color-gray-50)' : 'transparent',
                    transition: 'background-color 0.15s ease',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.backgroundColor = 'var(--color-gray-50)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 'var(--space-md)',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-sm)',
                        }}
                      >
                        <span
                          style={{
                            fontWeight: 600,
                            color: 'var(--color-gray-900)',
                          }}
                        >
                          {result.legalName}
                        </span>
                        {result.dbaName && (
                          <span
                            style={{
                              fontSize: '0.75rem',
                              color: 'var(--color-gray-500)',
                            }}
                          >
                            (DBA: {result.dbaName})
                          </span>
                        )}
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 'var(--space-md)',
                          marginTop: 'var(--space-xs)',
                          fontSize: '0.8125rem',
                          color: 'var(--color-gray-600)',
                        }}
                      >
                        <span>DOT: {result.dotNumber}</span>
                        <span>Units: {result.totalPowerUnits}</span>
                        <span>Drivers: {result.totalDrivers}</span>
                        {result.phyCity && result.phyState && (
                          <span>
                            {result.phyCity}, {result.phyState}
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                      {result.safetyRating && (
                        <span
                          className={`badge-modern ${
                            result.safetyRating === 'Satisfactory'
                              ? 'badge-success'
                              : result.safetyRating === 'Conditional'
                              ? 'badge-warning'
                              : 'badge-neutral'
                          }`}
                        >
                          {result.safetyRating}
                        </span>
                      )}
                      {!isSelected && (
                        <Plus
                          style={{
                            width: 20,
                            height: 20,
                            color: 'var(--color-primary)',
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* No Results */}
        {showResults && searchQuery.length >= 2 && !isSearching && searchResults.length === 0 && (
          <div
            ref={resultsRef}
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              marginTop: 'var(--space-xs)',
              background: '#fbf9f4',
              border: '1px solid var(--color-gray-200)',
              borderRadius: 'var(--radius-lg)',
              boxShadow: 'var(--shadow-lg)',
              padding: 'var(--space-lg)',
              textAlign: 'center',
              color: 'var(--color-gray-500)',
              zIndex: 50,
            }}
          >
            No carriers found for "{searchQuery}"
          </div>
        )}
      </div>

      {/* Error Message */}
      {searchError && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-sm)',
            padding: 'var(--space-sm) var(--space-md)',
            backgroundColor: '#f1e2da',
            border: '1px solid #f2dad2',
            borderRadius: 'var(--radius-md)',
            marginBottom: 'var(--space-md)',
            color: '#7a2b1e',
            fontSize: '0.875rem',
          }}
        >
          <AlertCircle style={{ width: 16, height: 16 }} />
          {searchError}
        </div>
      )}

      {/* Selected Carriers List */}
      <div style={{ marginBottom: 'var(--space-lg)' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 'var(--space-sm)',
          }}
        >
          <span
            style={{
              fontSize: '0.875rem',
              fontWeight: 500,
              color: 'var(--color-gray-700)',
            }}
          >
            Selected Carriers ({selectedCarriers.length}/{maxCarriers})
          </span>
          {selectedCarriers.length > 0 && (
            <button
              onClick={handleClearAll}
              style={{
                fontSize: '0.8125rem',
                color: 'var(--color-gray-500)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              Clear All
            </button>
          )}
        </div>

        {selectedCarriers.length === 0 ? (
          <div
            style={{
              padding: 'var(--space-xl)',
              background: 'var(--color-gray-50)',
              borderRadius: 'var(--radius-lg)',
              border: '2px dashed var(--color-gray-300)',
              textAlign: 'center',
            }}
          >
            <Truck
              style={{
                width: 48,
                height: 48,
                color: 'var(--color-gray-300)',
                margin: '0 auto var(--space-md)',
              }}
            />
            <p style={{ color: 'var(--color-gray-500)', fontSize: '0.9375rem' }}>
              No carriers selected yet
            </p>
            <p style={{ color: 'var(--color-gray-400)', fontSize: '0.8125rem', marginTop: 'var(--space-xs)' }}>
              Search above to add carriers for analysis
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
            {selectedCarriers.map((carrier, index) => (
              <div
                key={carrier.dotNumber}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-md)',
                  padding: 'var(--space-md)',
                  background: 'var(--color-gray-50)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--color-gray-200)',
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 'var(--radius-md)',
                    background:
                      index === 0
                        ? 'var(--color-primary)'
                        : 'var(--color-gray-300)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    flexShrink: 0,
                  }}
                >
                  {index === 0 ? 'A' : String.fromCharCode(66 + index - 1)}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      color: 'var(--color-gray-900)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {carrier.legalName}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 'var(--space-md)',
                      fontSize: '0.8125rem',
                      color: 'var(--color-gray-500)',
                      marginTop: 'var(--space-xs)',
                    }}
                  >
                    <span>DOT: {carrier.dotNumber}</span>
                    <span>Units: {carrier.totalPowerUnits}</span>
                    <span>Drivers: {carrier.totalDrivers}</span>
                  </div>
                </div>

                {carrier.safetyRating && (
                  <span
                    className={`badge-modern ${
                      carrier.safetyRating === 'Satisfactory'
                        ? 'badge-success'
                        : carrier.safetyRating === 'Conditional'
                        ? 'badge-warning'
                        : 'badge-neutral'
                    }`}
                    style={{ flexShrink: 0 }}
                  >
                    {carrier.safetyRating}
                  </span>
                )}

                <button
                  onClick={() => handleRemoveCarrier(carrier.dotNumber)}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-gray-300)',
                    background: '#fbf9f4',
                    color: 'var(--color-gray-500)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    transition: 'all 0.15s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#b3412c';
                    e.currentTarget.style.color = '#b3412c';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--color-gray-300)';
                    e.currentTarget.style.color = 'var(--color-gray-500)';
                  }}
                  title="Remove carrier"
                >
                  <X style={{ width: 16, height: 16 }} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Analyze Button */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingTop: 'var(--space-md)',
          borderTop: '1px solid var(--color-gray-200)',
        }}
      >
        <div style={{ fontSize: '0.8125rem', color: 'var(--color-gray-500)' }}>
          {selectedCarriers.length < minCarriers ? (
            <>Select at least {minCarriers} carriers to analyze</>
          ) : (
            <>Ready to analyze {selectedCarriers.length} carriers</>
          )}
        </div>

        <button
          onClick={onAnalyze}
          disabled={!canAnalyze}
          className="btn-primary"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-sm)',
            opacity: canAnalyze ? 1 : 0.5,
            cursor: canAnalyze ? 'pointer' : 'not-allowed',
          }}
        >
          {isAnalyzing ? (
            <>
              <Loader2
                style={{
                  width: 18,
                  height: 18,
                  animation: 'spin 1s linear infinite',
                }}
              />
              Analyzing...
            </>
          ) : (
            <>
              Analyze Merger
              <ChevronRight style={{ width: 18, height: 18 }} />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default CarrierSelector;
